import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Calendar, Clock, ArrowRight, User, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BlogSection = () => {
  const { t, language } = useLanguage();
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlogPosts();
  }, [language]);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API}/blog/posts`, {
        params: {
          language: language,
          size: 6,
          page: 1
        }
      });

      setBlogPosts(response.data);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Failed to load blog posts');
      // Fallback to mock data if API fails
      setBlogPosts(getMockBlogPosts());
    } finally {
      setLoading(false);
    }
  };

  // Fallback mock data in case API is not available
  const getMockBlogPosts = () => {
    return [
      {
        id: '1',
        title: t('blog.posts.post1.title'),
        excerpt: t('blog.posts.post1.excerpt'),
        author: 'Hack a Tech Team',
        created_at: '2024-12-01T00:00:00Z',
        read_time_minutes: 5,
        category: t('blog.categories.technology'),
        image_url: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=250&fit=crop&crop=entropy&auto=format',
        slug: 'modernizacao-tecnologica-2024',
        views: 150,
        featured: false,
        tags: ['modernização', 'tecnologia']
      },
      {
        id: '2',
        title: t('blog.posts.post2.title'),
        excerpt: t('blog.posts.post2.excerpt'),
        author: 'Hack a Tech Team',
        created_at: '2024-11-28T00:00:00Z',
        read_time_minutes: 7,
        category: t('blog.categories.cloud'),
        image_url: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400&h=250&fit=crop&crop=entropy&auto=format',
        slug: 'beneficios-cloud-aws-empresas',
        views: 230,
        featured: true,
        tags: ['aws', 'cloud']
      },
      {
        id: '3',
        title: t('blog.posts.post3.title'),
        excerpt: t('blog.posts.post3.excerpt'),
        author: 'Hack a Tech Team',
        created_at: '2024-11-25T00:00:00Z',
        read_time_minutes: 6,
        category: t('blog.categories.consulting'),
        image_url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop&crop=entropy&auto=format',
        slug: 'transformacao-digital-estrategica',
        views: 180,
        featured: false,
        tags: ['transformação digital', 'estratégia']
      }
    ];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(t('locale'), {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const categoryColors = {
      [t('blog.categories.technology')]: 'bg-primary/10 text-primary',
      [t('blog.categories.cloud')]: 'bg-blue-100 text-blue-700',
      [t('blog.categories.consulting')]: 'bg-secondary/10 text-secondary',
      [t('blog.categories.development')]: 'bg-green-100 text-green-700',
      [t('blog.categories.security')]: 'bg-red-100 text-red-700',
      [t('blog.categories.automation')]: 'bg-purple-100 text-purple-700',
      // Fallback for API category values
      'technology': 'bg-primary/10 text-primary',
      'cloud': 'bg-blue-100 text-blue-700',
      'consulting': 'bg-secondary/10 text-secondary',
      'development': 'bg-green-100 text-green-700',
      'security': 'bg-red-100 text-red-700',
      'automation': 'bg-purple-100 text-purple-700'
    };
    return categoryColors[category] || 'bg-gray-100 text-gray-700';
  };

  const translateCategory = (category) => {
    const categoryMap = {
      'technology': t('blog.categories.technology'),
      'cloud': t('blog.categories.cloud'),
      'consulting': t('blog.categories.consulting'),
      'development': t('blog.categories.development'),
      'security': t('blog.categories.security'),
      'automation': t('blog.categories.automation')
    };
    return categoryMap[category] || category;
  };

  if (loading) {
    return (
      <section id="blog" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('blog.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('blog.subtitle')}
            </p>
          </div>

          {/* Loading skeleton */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="w-full h-48 bg-gray-300 rounded-t-lg"></div>
                <CardContent className="p-6">
                  <div className="h-4 bg-gray-300 rounded mb-3"></div>
                  <div className="h-6 bg-gray-300 rounded mb-3"></div>
                  <div className="h-20 bg-gray-300 rounded mb-4"></div>
                  <div className="flex justify-between">
                    <div className="h-4 bg-gray-300 rounded w-20"></div>
                    <div className="h-4 bg-gray-300 rounded w-16"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="blog" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('blog.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('blog.subtitle')}
          </p>

          {error && (
            <div className="mt-4 flex items-center justify-center text-amber-600">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span className="text-sm">{t('blog.loadingFallback', 'Loading sample content...')}</span>
            </div>
          )}
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Card
              key={post.id}
              className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-2 overflow-hidden"
            >
              {/* Image */}
              <div className="relative overflow-hidden">
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=250&fit=crop&crop=entropy&auto=format';
                  }}
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                    {translateCategory(post.category)}
                  </span>
                </div>
                {post.featured && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-secondary text-white px-2 py-1 rounded-full text-xs font-medium">
                      ⭐ Featured
                    </span>
                  </div>
                )}
              </div>

              <CardContent className="p-6">
                {/* Meta Info */}
                <div className="flex items-center text-sm text-gray-500 mb-3 space-x-4">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {formatDate(post.created_at)}
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    {post.read_time_minutes} min
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary transition-colors duration-300 line-clamp-2">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                  {post.excerpt}
                </p>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Author and Read More */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-500">
                    <User className="w-4 h-4 mr-2" />
                    {post.author}
                  </div>

                  <Link
                    to={`/blog/${post.slug}`}
                    className="flex items-center text-primary hover:text-primary/80 font-medium text-sm group-hover:translate-x-1 transition-transform duration-300"
                  >
                    {t('blog.readMore')}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </div>

                {/* Views counter */}
                {post.views > 0 && (
                  <div className="mt-3 text-xs text-gray-400 text-right">
                    {post.views} {t('blog.views', 'views')}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* View All Posts Button */}
        <div className="text-center mt-12">
          <Link
            to="/blog"
            className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-300 group inline-flex items-center"
          >
            {t('blog.viewAll')}
            <ArrowRight className="inline-block w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </section>
  );
};